import { platformOf } from '../validation/bidHealth';

/**
 * launchScope.js — pure request-scope helpers for the Launch workspace
 * (pages/LaunchPage.jsx's ScopePanel).
 *
 * "Which requests is this criterion verified against?" used to have no answer a
 * tester could give: the Launch page took the Dashboard's row-picker handoff if
 * one happened to exist, and otherwise ran over the whole captured session. So
 * "review the Prebid requests from Media.net" was compiled and evaluated across
 * every vendor's Prebid traffic.
 *
 * The HOST facet is the one that does the real work here. determineRequestType()
 * on the proxy classifies by integration SHAPE, not by company — Media.net's
 * Prebid Server and the Xandr Prebid SDK are both requestType 'prebid' — so
 * filtering by request type alone cannot express "this vendor". The host can.
 *
 * Everything here is pure and total: bad input yields an empty/neutral result
 * rather than throwing, because these run inside render.
 */

/** Bucket label for a row with no parseable URL — a bucket, not a crash. */
export const NO_HOST = '(no host)';
/** Bucket label for a row the classifier left untyped. */
export const UNCLASSIFIED = '(unclassified)';

/**
 * Above this many distinct hosts, a Dashboard row-pick reads as a broad sample
 * rather than "this vendor", and deriving target.host from it would scope every
 * authored check to a host list the tester never reasoned about. Their explicit
 * picker choices are always sent, however many.
 */
export const MAX_DERIVED_HOSTS = 4;

/** Hostname of a captured row, lower-cased; NO_HOST when unparseable. */
export function hostOfRow(row) {
  try {
    return new URL(row?.url).hostname.toLowerCase();
  } catch {
    return NO_HOST;
  }
}

/** Platform of a captured row — iOS / Android / Other. Reuses the dashboard's
 *  classifier (platformClass / devicePlatform, then the GAM `iu` path), so the
 *  picker agrees with Bid Health's per-platform split. Works for logcat rows:
 *  they carry devicePlatform from the tap, or a platform segment in `iu`. */
export function platformOfRow(row) {
  try { return platformOf(row || {}) || 'Other'; } catch { return 'Other'; }
}

/** Request type of a captured row; UNCLASSIFIED when the classifier passed. */
export function typeOfRow(row) {
  return (row && row.requestType) || UNCLASSIFIED;
}

/**
 * facetCounts(rows, pick) -> [{ value, count }], busiest first.
 * Drives the picker's menu, so the tester sees how much traffic each choice
 * covers before choosing it.
 */
export function facetCounts(rows, pick) {
  const counts = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    let v;
    try { v = pick(r); } catch { continue; }
    counts.set(v, (counts.get(v) || 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count || String(a.value).localeCompare(String(b.value)));
}

/**
 * applyScope(rows, { types, hosts, platforms }) -> rows the compile/run should see.
 *
 * An EMPTY facet means "no constraint on this facet", not "match nothing" — so
 * the default (nothing picked) is the full set, and the two facets AND together
 * when both are set (type 'prebid' + host 'media.net' = that vendor's auctions,
 * which is the whole point).
 */
export function applyScope(rows, { types = [], hosts = [], platforms = [] } = {}) {
  const base = Array.isArray(rows) ? rows : [];
  const wantTypes = new Set(Array.isArray(types) ? types : []);
  const wantHosts = new Set(Array.isArray(hosts) ? hosts : []);
  const wantPlatforms = new Set(Array.isArray(platforms) ? platforms : []);
  if (!wantTypes.size && !wantHosts.size && !wantPlatforms.size) return base;
  return base.filter((r) => (
    (!wantTypes.size || wantTypes.has(typeOfRow(r)))
    && (!wantHosts.size || wantHosts.has(hostOfRow(r)))
    && (!wantPlatforms.size || wantPlatforms.has(platformOfRow(r)))
  ));
}

/**
 * deriveScopeDirective({ scope, handoffRows, usingHandoff })
 *   -> { types, hosts }
 *
 * What the tester EXPLICITLY picked, for the compiler's authoritative scope
 * directive (api/launches.js compileLaunch → proxy compileCriteria). Their own
 * filter always counts. A Dashboard row-pick contributes its request types
 * (mirroring ChecksPage.jsx's scopeTypes) and its hosts only when narrow enough
 * to read as one vendor — see MAX_DERIVED_HOSTS.
 *
 * Placeholder buckets never travel: '(unclassified)' is not a request type and
 * '(no host)' is not a host, and sending either would scope a check to a
 * literal that matches nothing on the proxy.
 */
export function deriveScopeDirective({ scope = {}, handoffRows = [], usingHandoff = false } = {}) {
  const picked = {
    types: Array.isArray(scope.types) ? scope.types : [],
    hosts: Array.isArray(scope.hosts) ? scope.hosts : [],
  };
  const rows = Array.isArray(handoffRows) ? handoffRows : [];

  let types = picked.types;
  if (!types.length && usingHandoff) {
    types = [...new Set(rows.map(typeOfRow))].filter((t) => t !== UNCLASSIFIED);
  }

  let hosts = picked.hosts;
  if (!hosts.length && usingHandoff) {
    const derived = [...new Set(rows.map(hostOfRow))].filter((h) => h !== NO_HOST);
    hosts = derived.length && derived.length <= MAX_DERIVED_HOSTS ? derived : [];
  }

  return {
    types: types.filter((t) => t !== UNCLASSIFIED),
    hosts: hosts.filter((h) => h !== NO_HOST),
  };
}
